/*
 * AppConsent
 *
 * The privacy question the embedded app has asked this site to put, and the
 * answer on its way back.
 *
 * The app owns the question. Which regime applies, what the wording is, which
 * actions exist and what an answer means are all decided inside the app, where
 * the locale and the consent record live. Duplicating any of that here would
 * mean two answers to the same legal question, free to drift apart.
 *
 * This site owns the rendering, and it renders at the ROOT rather than inside
 * the frame view. A prompt drawn inside the component that hosts the iframe is
 * scoped to that component: it sits in the app's corner of the page, it goes
 * away the moment somebody navigates off the app route, and it competes for
 * stacking context with the frame it is meant to sit above. Consent is a
 * property of the visit, not of one page, so it belongs at the top.
 *
 * Which leaves the frame view holding only what it is uniquely able to do -
 * talk to the frame. It puts requests in here and takes answers out. Same
 * listener shape as AppNavigation and Route, for the same reason: one place the
 * value lives, everything else subscribes.
 */

const listeners = [];
const answer_listeners = [];

// The outstanding request, or null when there is none. Held opaque on purpose -
// nothing on this side reads into it beyond `required`, which decides whether
// the modal may be closed without answering.
let current = null;

const AppConsent = {

    get: () => current,

    set: next => {
        current = next || null;
        listeners.forEach(listener => {
            try {
                listener(current);
            } catch(e) {
                console.error(`App consent listener failed: ${e.message}`);
            }
        });
    },

    subscribe: listener => {
        listeners.push(listener);
        return () => {
            let index = listeners.indexOf(listener);
            if(index >= 0) {
                listeners.splice(index, 1);
            }
        };
    },

    /*
     * An answer, or a dismissal.
     *
     * `key` is whichever button the app sent and the visitor pressed; null
     * means the modal was closed without answering, which the app only permits
     * when a choice already exists. Passing the key back rather than a boolean
     * keeps the meaning of each action in the app - this side never has to know
     * that "accept" is the one that turns analytics on.
     */
    respond: key => {
        current = null;
        answer_listeners.forEach(listener => {
            try {
                listener(key);
            } catch(e) {
                console.error(`App consent answer listener failed: ${e.message}`);
            }
        });
        listeners.forEach(listener => {
            try {
                listener(null);
            } catch(e) {
                console.error(`App consent listener failed: ${e.message}`);
            }
        });
    },

    subscribeToAnswer: listener => {
        answer_listeners.push(listener);
        return () => {
            let index = answer_listeners.indexOf(listener);
            if(index >= 0) {
                answer_listeners.splice(index, 1);
            }
        };
    }
};

export default AppConsent;
